import { Link } from "react-router-dom";
import { ArrowRight, Upload, Scale } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import FaqAccordion from "@/components/sections/FaqAccordion";
import TrustBadges from "@/components/sections/TrustBadges";
import { OPTIONS, ROWS, COMPARE_FAQS, UPDATED } from "@/lib/compareData";
import { comparePageSchema } from "@/lib/structuredData";

export default function Compare() {
  return (
    <div data-testid="page-compare">
      <SEO
        title="Carfornia vs CarMax vs Carvana vs Dealer Trade-In — Honest Comparison for Bay Area Sellers"
        description="Selling a car in the Bay Area? An honest side-by-side of Carfornia Buyers, CarMax, Carvana, dealer trade-in and private sale: how you get a number, where the inspection happens, who is really buying, payment, paperwork and the downsides of each."
        canonical="https://carforniabuyers.com/compare"
        jsonLd={comparePageSchema()}
      />

      <section className="bg-brand-navy text-brand-warm relative overflow-hidden">
        <div className="absolute inset-0 bg-grain opacity-30" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16 lg:pt-32 lg:pb-20">
          <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/10 border border-white/15 text-[11px] font-bold uppercase tracking-[0.2em] reveal reveal-1"><Scale className="w-3.5 h-3.5 text-brand-orange" /> Honest comparison · updated {UPDATED}</span>
          <h1 className="font-display font-extrabold tracking-[-0.03em] leading-[1] text-[clamp(2.2rem,4.4vw,4rem)] mt-6 reveal reveal-2" data-testid="compare-headline">
            Carfornia vs CarMax vs Carvana vs dealer trade-in: <span className="text-brand-blue brightness-150">which is best for selling your car?</span>
          </h1>
          <p className="mt-7 text-lg text-blue-100/85 leading-relaxed max-w-3xl reveal reveal-3" data-testid="compare-intro">
            Short answer: it depends on your car and how much hassle you want. Below is how each option really works — including the downsides of selling to us. Competitor details describe their generally published processes; check their sites for current terms.
          </p>
        </div>
      </section>

      <section className="py-14 lg:py-20 bg-white" data-testid="compare-table-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="overflow-x-auto rounded-2xl border border-slate-200 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
            <table className="w-full min-w-[1040px] text-sm border-collapse" data-testid="compare-table">
              <thead>
                <tr className="bg-brand-surface">
                  <th className="text-left p-4 w-44 text-xs font-bold uppercase tracking-wider text-slate-500 align-bottom">Compare</th>
                  {OPTIONS.map((o) => (
                    <th key={o.id} className={`text-left p-4 align-bottom ${o.highlight ? "bg-blue-50 border-t-4 border-brand-blue" : ""}`}>
                      <div className={`font-display text-base font-extrabold ${o.highlight ? "text-brand-blue" : "text-slate-900"}`}>{o.name}</div>
                      <div className="text-xs font-normal text-slate-500 mt-0.5">{o.sub}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ROWS.map((r, i) => (
                  <tr key={r.label} className={i % 2 ? "bg-slate-50/60" : "bg-white"} data-testid={`compare-row-${i}`}>
                    <th scope="row" className="text-left p-4 font-bold text-slate-900 align-top">{r.label}</th>
                    {OPTIONS.map((o) => (
                      <td key={o.id} className={`p-4 align-top text-slate-700 leading-relaxed ${o.highlight ? "bg-blue-50/50 font-medium text-slate-900" : ""}`}>{r.cells[o.id]}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-slate-500 mt-4">CarMax and Carvana are trademarks of their respective owners; we are not affiliated with them. Descriptions reflect our understanding of their standard processes as of {UPDATED} and may change.</p>
        </div>
      </section>

      <section className="py-16 lg:py-20 bg-brand-surface" data-testid="compare-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-blue">The fair way to decide</p>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight mt-3">Get their number. Then let us try to beat it.</h2>
            <p className="text-slate-600 mt-4 leading-relaxed text-lg">There is no downside: get a CarMax or Carvana offer, upload it in step 2 of our form, and we will tell you honestly whether we can do better — before we come out. If we cannot, take their offer with our blessing.</p>
          </div>
          <div className="lg:col-span-5">
            <div className="bg-white rounded-3xl p-7 border border-slate-100 shadow-[0_20px_60px_-20px_rgba(0,30,102,0.25)]">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-orange-50 text-brand-orange mb-4"><Upload className="w-6 h-6" /></div>
              <h3 className="font-display text-2xl font-extrabold text-slate-900">Have a written offer already?</h3>
              <p className="text-slate-600 mt-2">Photo or PDF. Takes two minutes.</p>
              <Link to="/sell-your-car"><Button data-testid="compare-upload-cta" className="w-full h-13 mt-5 bg-[#FF6B00] hover:bg-[#E66000] text-white font-bold rounded-xl text-base">Upload my offer <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"><TrustBadges /></section>

      <section className="py-20 bg-white" data-testid="compare-faq">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-blue">Questions people ask</p>
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight mt-3">Who pays the most, trade-in vs sell, smog and more.</h2>
          <div className="mt-10"><FaqAccordion items={COMPARE_FAQS} testId="compare-faq-accordion" /></div>
        </div>
      </section>
    </div>
  );
}
